import layout from '@/pages/Settings/AiAgents/components/agentLayout.module.css';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Select, Spin, Form, Divider, Checkbox } from 'antd';
import type { RadioChangeEvent } from 'antd';
import {
  AIChannelType,
  AIAgentType,
  IntervalUnit,
  LBNodeData,
} from '@/proto/logiblocks/logiblocks';
import {
  customerLegEnabled,
  FieldErrorLabel,
  useShipmentContactFilter,
  WaitDurationField,
  withLogiblocksContactRoles,
} from '@/pages/Logiblocks/nodes/shared';
import {
  USER_VARIABLES_FIELD,
  type ValidationError,
} from '@/pages/Logiblocks/validation';
import styles from '../NodeCustomization.module.css';
import { CustomizationComponent } from '@/pages/Logiblocks/types';
import useToken from '@/common/helper/useToken';
import {
  GetAgentsWithPaginationAndSearch,
  GetUsersByTeamIds,
  GetAgentById,
  GetAiAgentDropdownsOptions,
} from '@/apis/settings';
import { decodeCollectVariableDescriptors } from './collectVariableDescriptors';
import {
  ApiAgent,
  EditAgent,
  AgentDrawerFormValues,
  ApiCollect,
  ApiEscalation,
  ApiVoice,
} from '@/pages/Settings/AiAgents/types';
import { TeamWithUsers, AdditionalRecipientOption } from '@/apis/types';
import CustomRadioGroup from '@/components/CustomRadioGroup';
import { ChannelSection } from '@/pages/Settings/AiAgents/components/ChannelSection';
import {
  mapEditAgentToFormValues,
  collectVarsToCollectIds,
  parseResolutionPrompt,
} from '@/pages/Settings/AiAgents/agentFormMappers';
import CustomInput from '@/components/CustomInput';
import CheckboxMultiSelect from '@/components/CheckboxMultiSelect';
import ToggleSwitch from '@/components/ToggleSwitch';
import { EscalationSection } from '@/pages/Settings/AiAgents/components/EscalateSection';
import { PhoneTreeSection } from '@/pages/Settings/AiAgents/components/PhoneTreeSection';
import { renderInstructionsPreview } from '@/pages/Settings/AiAgents/components/DynMentionFields';
import { useDynamicVariables } from '@/pages/Settings/AiAgents/hooks/useDynamicVariables';
import {
  CallFillIcon,
  MailFillIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MaleIcon,
  FemaleIcon,
  InboundIcon,
  OutboundIcon,
} from '@/assets/icons/icons';
import '@/pages/Settings/AiAgents/AgentsTable.css';

interface ContactOption {
  value: string;
  label: string;
  disabled?: boolean;
}

// Shared by the dropdown rows and the closed Select so both show the same text.
const formatAgentLabel = (agent: ApiAgent) =>
  `${agent.name || agent.id}${agent.purpose_name ? ` (${agent.purpose_name})` : ''}`;

/**
 * Read-only display of AI agent details using the same structure as Create/Edit page
 */
const AgentDetailsReadOnly: React.FC<{
  agent: EditAgent;
  collectOptions: Array<{ value: string; label: string }>;
  escalateWhenOptions: Array<{ value: string; label: string }>;
  userVariablesError?: ValidationError;
}> = ({ agent, collectOptions, escalateWhenOptions, userVariablesError }) => {
  const [form] = Form.useForm<AgentDrawerFormValues>();
  const [behaviorCollapsed, setBehaviorCollapsed] = useState(false);
  const [successCollapsed, setSuccessCollapsed] = useState(false);
  const [callExpanded, setCallExpanded] = useState(true);
  const [emailExpanded, setEmailExpanded] = useState(true);
  const { token } = useToken();
  const { dynVarOptions } = useDynamicVariables(token);

  const formValues = useMemo(() => {
    if (!agent) return null;
    return mapEditAgentToFormValues(agent);
  }, [agent]);

  useEffect(() => {
    if (formValues) {
      form.setFieldsValue(formValues);
    }
  }, [formValues, form]);

  const voiceData = agent.voice as ApiVoice | null;

  if (!formValues) return null;

  const hasPhone = agent.channel?.some((c) => c.type === 'phone');
  const hasEmail = agent.channel?.some((c) => c.type === 'email');
  const collectedInfo = (agent.collect || []).map((collectItem) => {
    const collectId = String(collectItem.collect_id);
    const option = collectOptions.find(
      (opt) => String(opt.value) === collectId
    );
    return option?.label || collectId;
  });

  // Parse resolution prompt
  const resolutionPromptData = parseResolutionPrompt(agent.resolution_prompt);
  const isCustomSuccess = resolutionPromptData?.type === 'custom';
  const customSuccessText = isCustomSuccess
    ? resolutionPromptData.custom_text
    : '';

  const presets =
    !isCustomSuccess && resolutionPromptData?.type === 'preset'
      ? resolutionPromptData.presets
      : [];

  const infoCollectedPreset = presets.find((p) => p.id === 'info_collected');
  const noInterventionPreset = presets.find((p) => p.id === 'no_intervention');
  const positiveSentimentPreset = presets.find(
    (p) => p.id === 'positive_sentiment'
  );

  const successCollectVars = collectVarsToCollectIds(
    infoCollectedPreset?.collect_vars?.map(String),
    agent.collect
  );

  return (
    <div className={styles.readOnlyAgentDetails}>
      <Form form={form} layout='vertical'>
        {/* Agent Behavior Section */}
        <div
          className={['agentBehaviorCard', layout.sectionSpacing]
            .filter(Boolean)
            .join(' ')}
        >
          <div className='agentBehaviorHeader'>
            <div className='agentBehaviorHeader__title'>Agent behavior</div>
            <button
              type='button'
              className='agentBehaviorHeader__chevron'
              aria-label='Toggle Agent behavior'
              aria-expanded={!behaviorCollapsed}
              aria-controls='agent-behavior-panel'
              onClick={() => setBehaviorCollapsed(!behaviorCollapsed)}
            >
              {behaviorCollapsed ? (
                <ChevronDownIcon size={16} />
              ) : (
                <ChevronUpIcon size={16} />
              )}
            </button>
          </div>

          {!behaviorCollapsed && (
            <div id='agent-behavior-panel' className='agentBehaviorBody'>
              <Divider
                className='agentSuccessOrDivider'
                style={{ marginTop: 16, marginBottom: 0 }}
              />
              <div className='agentBehaviorLabel'>Agent instructions</div>
              <div className='agentFormIntroPreview'>
                {agent.behavior_prompt?.trim()
                  ? renderInstructionsPreview(
                      agent.behavior_prompt,
                      dynVarOptions
                    )
                  : 'No instructions provided'}
              </div>

              <div className='agentBehaviorLabel agentBehaviorLabel--spaced'>
                Information to be collected
              </div>
              <ul className='agentBehaviorList'>
                {collectedInfo.length > 0 ? (
                  collectedInfo.map((info) => (
                    <li key={info} className='agentBehaviorList__item'>
                      {info}
                    </li>
                  ))
                ) : (
                  <li className='agentBehaviorList__item'>None</li>
                )}
              </ul>
              <FieldErrorLabel error={userVariablesError} />
            </div>
          )}
        </div>

        {/* Agent Success Criteria Section */}
        {agent.resolution_prompt && (
          <div
            className={['agentBehaviorCard', layout.sectionSpacing]
              .filter(Boolean)
              .join(' ')}
          >
            <div className='agentBehaviorHeader'>
              <div className='agentBehaviorHeader__title'>
                Agent success criteria
              </div>
              <button
                type='button'
                className='agentBehaviorHeader__chevron'
                aria-label='Toggle Agent success criteria'
                aria-expanded={!successCollapsed}
                aria-controls='agent-success-panel'
                onClick={() => setSuccessCollapsed(!successCollapsed)}
              >
                {successCollapsed ? (
                  <ChevronDownIcon size={16} />
                ) : (
                  <ChevronUpIcon size={16} />
                )}
              </button>
            </div>

            {!successCollapsed && (
              <div id='agent-success-panel' className='agentBehaviorBody'>
                <Divider
                  className='agentSuccessOrDivider'
                  style={{ marginTop: 16, marginBottom: 0 }}
                />

                {isCustomSuccess ? (
                  <>
                    <div
                      className={['agentBehaviorLabel', layout.labelSpacing]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      Custom success criteria
                    </div>
                    <div className='agentFormIntroPreview'>
                      {customSuccessText || 'No custom criteria provided'}
                    </div>
                  </>
                ) : (
                  <>
                    <div className='agentBehaviorLabel'>
                      Specific success criteria
                    </div>
                    <div className='agentSuccessSpecific'>
                      <div className='agentSuccessCheckRow'>
                        <Checkbox
                          className='custom-menu-shipment-checkbox-only'
                          checked={!!infoCollectedPreset}
                          disabled
                        >
                          <span className='agentSuccessCheckLabel'>
                            Information successfully collected:
                          </span>
                        </Checkbox>
                      </div>

                      {infoCollectedPreset && (
                        <CheckboxMultiSelect
                          titleText='Information successfully collected:'
                          options={collectOptions}
                          value={successCollectVars}
                          className='agentSelect agentSelect--darkText'
                          disabled={true}
                        />
                      )}

                      <div className='agentSuccessCheckRow'>
                        <Checkbox
                          className='custom-menu-shipment-checkbox-only'
                          checked={!!noInterventionPreset}
                          disabled
                        >
                          <span className='agentSuccessCheckLabel'>
                            No human intervention was needed
                          </span>
                        </Checkbox>
                      </div>

                      <div className='agentSuccessCheckRow'>
                        <Checkbox
                          className='custom-menu-shipment-checkbox-only'
                          checked={!!positiveSentimentPreset}
                          disabled
                        >
                          <span className='agentSuccessCheckLabel'>
                            Neutral or positive sentiment
                          </span>
                        </Checkbox>
                      </div>

                      {presets.length === 0 && (
                        <div className={layout.emptyHint}>
                          No specific criteria defined
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Retrieve Shipment Info */}
        {agent.collect_shipment_id && (
          <>
            <Divider className='agentFormDivider' />
            <div
              className={['agentRetrieveShipmentRow', layout.retrievalRow]
                .filter(Boolean)
                .join(' ')}
            >
              <div className='agentRetrieveShipmentRow__left'>
                <svg
                  width='14'
                  height='11'
                  viewBox='0 0 14 11'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  className={layout.checkboxAlign}
                >
                  <path
                    d='M1.33333 2V3.33333C1.33333 3.7 1.03333 4 0.666667 4H0V6.66667H0.666667C1.03333 6.66667 1.33333 6.96667 1.33333 7.33333V8.66667C1.33333 9.76667 2.23333 10.6667 3.33333 10.6667H5.33333V9.33333H3.33333C2.96667 9.33333 2.66667 9.03333 2.66667 8.66667V7.33333C2.66667 6.46667 2.10667 5.72 1.33333 5.44667V5.22C2.10667 4.94667 2.66667 4.2 2.66667 3.33333V2C2.66667 1.63333 2.96667 1.33333 3.33333 1.33333H5.33333V0H3.33333C2.23333 0 1.33333 0.9 1.33333 2Z'
                    fill='var(--text-primary)'
                  />
                  <path
                    d='M12.6667 4C12.3 4 12 3.7 12 3.33333V2C12 0.9 11.1 0 10 0H8V1.33333H10C10.3667 1.33333 10.6667 1.63333 10.6667 2V3.33333C10.6667 4.2 11.2267 4.94667 12 5.22V5.44667C11.2267 5.72 10.6667 6.46 10.6667 7.33333V8.66667C10.6667 9.03333 10.3667 9.33333 10 9.33333H8V10.6667H10C11.1 10.6667 12 9.76667 12 8.66667V7.33333C12 6.96667 12.3 6.66667 12.6667 6.66667H13.3333V4H12.6667Z'
                    fill='var(--text-primary)'
                  />
                </svg>
                <div>
                  <div className='agentFormToggleLabel'>
                    Retrieve Shipment Info via MCP
                  </div>
                  <p
                    className={['agentFormHelpText', layout.helpSpacing]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    Matches the conversation to a shipment for reference in
                    later steps
                  </p>
                </div>
              </div>
              <ToggleSwitch
                label=''
                checked={true}
                onChange={() => {}}
                disabled
              />
            </div>
          </>
        )}

        {/* Call Section */}
        {hasPhone && (
          <div className={layout.noMargin}>
            <Divider className='agentFormDivider' />
            <div className='agentChannelCard'>
              <button
                type='button'
                className='agentChannelHeader'
                aria-expanded={callExpanded}
                aria-controls='call-channel-panel'
                onClick={() => setCallExpanded(!callExpanded)}
              >
                <div className='agentChannelHeader__left'>
                  <span className='agentChannelHeader__icon'>
                    <CallFillIcon size={16} />
                  </span>
                  <span className='agentChannelHeader__title'>Call</span>
                </div>
                <div className='agentChannelHeader__right'>
                  <span className='agentChannelHeader__chevron'>
                    {callExpanded ? (
                      <ChevronUpIcon size={24} />
                    ) : (
                      <ChevronDownIcon size={24} />
                    )}
                  </span>
                </div>
              </button>

              {callExpanded && (
                <div id='call-channel-panel' className='agentChannelBody'>
                  <div className={layout.stack}>
                    <ChannelSection channelType='phone' form={form} disabled />

                    {voiceData && (
                      <Form.Item label='Voice options'>
                        <div className={layout.destinationPill}>
                          {voiceData.order && (
                            <span className={layout.mutedLabel}>
                              {voiceData.order}
                            </span>
                          )}
                          <span>
                            {voiceData.gender === 'male' ? (
                              <MaleIcon />
                            ) : (
                              <FemaleIcon />
                            )}
                          </span>
                          <span className={layout.bodyText}>
                            {voiceData.accent}, {voiceData.age}
                          </span>
                        </div>
                      </Form.Item>
                    )}

                    <EscalationSection
                      form={form}
                      escalateWhenOptions={escalateWhenOptions}
                      loadingDropdowns={false}
                      isDefaultAgent={false}
                      readOnly={true}
                    />
                    {agent.agent_type === 'Inbound' && (
                      <PhoneTreeSection form={form} disabled />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Email Section */}
        {hasEmail && (
          <div className={layout.noMargin}>
            <Divider className='agentFormDivider' />
            <div className='agentChannelCard'>
              <button
                type='button'
                className='agentChannelHeader'
                aria-expanded={emailExpanded}
                aria-controls='email-channel-panel'
                onClick={() => setEmailExpanded(!emailExpanded)}
              >
                <div className='agentChannelHeader__left'>
                  <span className='agentChannelHeader__icon'>
                    <MailFillIcon size={16} />
                  </span>
                  <span className='agentChannelHeader__title'>Email</span>
                </div>
                <div className='agentChannelHeader__right'>
                  <span className='agentChannelHeader__chevron'>
                    {emailExpanded ? (
                      <ChevronUpIcon size={24} />
                    ) : (
                      <ChevronDownIcon size={24} />
                    )}
                  </span>
                </div>
              </button>

              {emailExpanded && (
                <div id='email-channel-panel' className='agentChannelBody'>
                  <div className={layout.stack}>
                    <ChannelSection channelType='email' form={form} disabled />

                    {agent.cc_email && (
                      <Form.Item
                        label="CC'ed email"
                        className='custom-input form-input-style'
                      >
                        <CustomInput
                          disabled
                          value={agent.cc_email}
                          placeholder='john@customer.com'
                          bgColor='var(--light-gray)'
                          height='36px'
                          textColor='var(--text-primary)'
                          borderRadius='8px'
                          border='none'
                          placeholderColor='var(--text-secondary)'
                        />
                      </Form.Item>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Form>
    </div>
  );
};

/**
 * Customization UI for AI_CALL_ACTION nodes.
 */
export const AICallActionCustomization: CustomizationComponent = ({
  nodeData,
  nodeId,
  onChange,
  isReadOnly = false,
  validationErrors,
}) => {
  const { token } = useToken();
  const userVariablesError = validationErrors?.find(
    (e) => e.field === USER_VARIABLES_FIELD
  );

  const rawAICallData = nodeData.ai_call_action;
  const currentAICallData = useMemo(
    () => ({
      channel_type: rawAICallData?.channel_type ?? AIChannelType.CALL,
      agent_type: rawAICallData?.agent_type ?? AIAgentType.RECOMMENDED,
      agent_id: rawAICallData?.agent_id ?? '',
      contact_id: rawAICallData?.contact_id ?? '',
      is_inbound_agent: rawAICallData?.is_inbound_agent ?? false,
      pca_variable_names: [] as string[],
      collect_shipment_id: rawAICallData?.collect_shipment_id ?? false,
      is_transfer_escalation: rawAICallData?.is_transfer_escalation ?? false,
      reply_wait_seconds: rawAICallData?.reply_wait_seconds ?? 0,
      dont_wait: rawAICallData?.dont_wait ?? false,
    }),
    [rawAICallData]
  );

  const [channelType, setChannelType] = useState(
    currentAICallData.channel_type ?? AIChannelType.CALL
  );
  const [agentType, setAgentType] = useState(
    currentAICallData.agent_type ?? AIAgentType.RECOMMENDED
  );
  const [agentId, setAgentId] = useState(currentAICallData.agent_id || '');
  const [contactId, setContactId] = useState(
    currentAICallData.contact_id || ''
  );

  const [agents, setAgents] = useState<ApiAgent[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [agentsLoadError, setAgentsLoadError] = useState(false);
  const [contacts, setContacts] = useState<ContactOption[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedAgentDetails, setSelectedAgentDetails] =
    useState<EditAgent | null>(null);
  const [loadingAgentDetails, setLoadingAgentDetails] = useState(false);
  const [collectOptions, setCollectOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [escalateWhenOptions, setEscalateWhenOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);

  const notifyChange = useCallback(
    (
      updates: Partial<typeof currentAICallData>,
      extraNodeData?: Partial<LBNodeData>
    ) => {
      onChange({
        ai_call_action: {
          ...currentAICallData,
          ...updates,
        },
        ...extraNodeData,
      });
    },
    [currentAICallData, onChange]
  );

  useEffect(() => {
    const aiCallData = nodeData.ai_call_action || {
      channel_type: AIChannelType.CALL,
      agent_type: AIAgentType.RECOMMENDED,
      agent_id: '',
      contact_id: '',
    };
    setChannelType(aiCallData.channel_type ?? AIChannelType.CALL);
    setAgentType(aiCallData.agent_type ?? AIAgentType.RECOMMENDED);
    setAgentId(aiCallData.agent_id || '');
    setContactId(aiCallData.contact_id || '');
  }, [nodeData]);

  useEffect(() => {
    if (!token) return;

    setLoadingAgents(true);
    setAgentsLoadError(false);
    GetAgentsWithPaginationAndSearch(token, '', 1)
      .then((res) => {
        const agentsList = res?.data?.data?.data || [];
        const filteredAgents = agentsList.filter(
          (agent) => agent.purpose_name !== 'Default'
        );
        setAgents(filteredAgents);
      })
      .catch((error) => {
        console.error('Error fetching agents:', error);
        setAgentsLoadError(true);
      })
      .finally(() => {
        setLoadingAgents(false);
      });
  }, [token]);

  useEffect(() => {
    if (!token) return;

    GetAiAgentDropdownsOptions(token)
      .then((res) => {
        const data = (res?.data?.data?.data || {}) as {
          collect?: ApiCollect[];
          escalation?: ApiEscalation[];
        };
        const collectData = data.collect || [];
        const escalationData = data.escalation || [];

        setCollectOptions(
          collectData
            .filter((c) => !c.is_archived)
            .map((c) => ({
              value: String(c.id),
              label: c.display_name,
            }))
        );

        setEscalateWhenOptions(
          escalationData
            .filter((e) => !e.is_archived)
            .map((e) => ({
              value: String(e.id),
              label: e.name,
            }))
        );
      })
      .catch((error) => {
        console.error('Error fetching dropdown options:', error);
      });
  }, [token]);

  useEffect(() => {
    if (!token) return;

    setLoadingContacts(true);
    // Fetch company contacts - pass empty array to get all teams/users
    GetUsersByTeamIds(token, [])
      .then((res) => {
        const data = (res?.data?.data?.data || {}) as {
          additional?: AdditionalRecipientOption[];
          teams_users?: TeamWithUsers[];
        };
        const roleOptions: ContactOption[] = [];
        // Add additional recipients (like transport provider contacts)
        const additional = data.additional || [];
        additional.forEach((item) => {
          if (item.enabled !== false) {
            roleOptions.push({
              value: item.key,
              label: item.label,
              disabled: !item.enabled,
            });
          }
        });
        const contactOptions: ContactOption[] = withLogiblocksContactRoles(
          roleOptions,
          { customerLeg: customerLegEnabled() }
        );

        // Add users from all teams
        const teamsUsers = data.teams_users || [];
        teamsUsers.forEach((team) => {
          const users = team.users || [];
          users.forEach((user) => {
            contactOptions.push({
              value: `${team.id}::${user.id}`,
              label: user.name || user.email || user.id,
            });
          });
        });

        setContacts(contactOptions);
      })
      .catch((error) => {
        console.error('Error fetching contacts:', error);
        setContacts([]);
      })
      .finally(() => {
        setLoadingContacts(false);
      });
  }, [token]);

  // Fetch selected agent details (only for custom agents)
  useEffect(() => {
    if (!token || !agentId || agentId === 'default') {
      setSelectedAgentDetails(null);
      return;
    }

    setLoadingAgentDetails(true);
    GetAgentById(token, agentId)
      .then((res) => {
        const agentData = res?.data?.data?.data as EditAgent | undefined;
        setSelectedAgentDetails(agentData || null);

        if (agentData?.collect) {
          const pcaUserVars = decodeCollectVariableDescriptors(
            agentData.collect,
            'agent.collect'
          );

          const currentUserVars = nodeData.user_variables ?? [];
          const changed =
            pcaUserVars.length !== currentUserVars.length ||
            pcaUserVars.some(
              (uv, i) =>
                uv.name !== currentUserVars[i]?.name ||
                uv.display_name !== currentUserVars[i]?.display_name ||
                uv.type !== currentUserVars[i]?.type ||
                uv.description !== currentUserVars[i]?.description ||
                uv.sample_value !== currentUserVars[i]?.sample_value ||
                uv.is_multi_select !== currentUserVars[i]?.is_multi_select ||
                JSON.stringify(uv.choices) !==
                  JSON.stringify(currentUserVars[i]?.choices)
            );

          if (changed) {
            onChange({
              user_variables: pcaUserVars,
              ai_call_action: {
                ...currentAICallData,
                collect_shipment_id: agentData.collect_shipment_id ?? false,
              },
            });
          } else if (
            (agentData.collect_shipment_id ?? false) !==
            (currentAICallData.collect_shipment_id ?? false)
          ) {
            notifyChange({
              collect_shipment_id: agentData.collect_shipment_id ?? false,
            });
          }
        }
      })
      .catch((error) => {
        console.error('Error fetching agent details:', error);
        setSelectedAgentDetails(null);
      })
      .finally(() => {
        setLoadingAgentDetails(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, agentId]);

  const handleChannelTypeChange = (e: RadioChangeEvent) => {
    const newChannelType = e.target.value as AIChannelType;
    setChannelType(newChannelType);
    const selectedAgent = agents.find((a) => a.id === agentId);
    const hasPhone = !!selectedAgent?.channel?.some((c) => c.type === 'phone');
    const hasEmail = !!selectedAgent?.channel?.some((c) => c.type === 'email');
    const stillValid =
      newChannelType === AIChannelType.CALL
        ? hasPhone
        : newChannelType === AIChannelType.EMAIL
          ? hasEmail
          : hasPhone && hasEmail;
    // "Don't wait" only exists for email, and the reply wait for steps that can
    // email; clear them so they can't apply (or block publishing) unseen.
    const dontWait =
      newChannelType === AIChannelType.EMAIL && currentAICallData.dont_wait;
    const replyWaitSeconds =
      newChannelType === AIChannelType.CALL
        ? 0
        : currentAICallData.reply_wait_seconds;
    if (!stillValid) {
      setAgentId('');
      notifyChange({
        channel_type: newChannelType,
        agent_id: '',
        dont_wait: dontWait,
        reply_wait_seconds: replyWaitSeconds,
      });
    } else {
      notifyChange({
        channel_type: newChannelType,
        dont_wait: dontWait,
        reply_wait_seconds: replyWaitSeconds,
      });
    }
  };

  const handleAgentTypeChange = (e: RadioChangeEvent) => {
    const newAgentType = e.target.value as AIAgentType;
    setAgentType(newAgentType);
    const newAgentId =
      newAgentType === AIAgentType.RECOMMENDED ? 'default' : '';
    setAgentId(newAgentId);
    notifyChange(
      {
        agent_type: newAgentType,
        agent_id: newAgentId,
        collect_shipment_id: false,
        dont_wait: false,
      },
      { user_variables: [] }
    );
  };

  const handleAgentIdChange = (newAgentId: string) => {
    setAgentId(newAgentId);
    const agentIsInbound =
      agents.find((a) => a.id === newAgentId)?.direction === 'Inbound';
    notifyChange(
      {
        agent_id: newAgentId,
        is_inbound_agent: agentIsInbound,
        collect_shipment_id: false,
      },
      { user_variables: [] }
    );
  };

  const handleContactIdChange = (newContactId: string) => {
    setContactId(newContactId);
    notifyChange({ contact_id: newContactId });
  };

  const { visibleContacts, hasUpstreamShipment } = useShipmentContactFilter(
    nodeId,
    contacts,
    contactId,
    handleContactIdChange
  );

  useEffect(() => {
    if (!hasUpstreamShipment && agentType === AIAgentType.RECOMMENDED) {
      setAgentType(AIAgentType.CUSTOMIZED);
      setAgentId('');
      notifyChange(
        { agent_type: AIAgentType.CUSTOMIZED, agent_id: '' },
        { user_variables: [] }
      );
    }
  }, [hasUpstreamShipment, agentType, notifyChange]);

  const isInboundAgent =
    agentType === AIAgentType.CUSTOMIZED && currentAICallData.is_inbound_agent;

  const showRecipient =
    agentType === AIAgentType.RECOMMENDED ||
    (agentType === AIAgentType.CUSTOMIZED && !!agentId && !isInboundAgent);

  // The reply settings only affect emails: a call's result arrives when the call ends.
  const showReplySettings =
    showRecipient &&
    (channelType === AIChannelType.EMAIL ||
      channelType === AIChannelType.USER_PREFERENCE);
  const showDontWait =
    channelType === AIChannelType.EMAIL && agentType === AIAgentType.CUSTOMIZED;
  const dontWait = showDontWait && currentAICallData.dont_wait;

  return (
    <>
      <div className={styles.customizationSection}>
        <label className={styles.sectionLabel}>Channel Type</label>
        <CustomRadioGroup
          value={channelType}
          onChange={handleChannelTypeChange}
          disabled={isReadOnly}
          options={[
            { value: AIChannelType.CALL, label: 'Call' },
            { value: AIChannelType.EMAIL, label: 'Email' },
            { value: AIChannelType.USER_PREFERENCE, label: 'User Preference' },
          ]}
        />
      </div>

      <div className={styles.customizationSection}>
        <label className={styles.sectionLabel}>Agent Type</label>
        <CustomRadioGroup
          value={agentType}
          onChange={handleAgentTypeChange}
          disabled={isReadOnly}
          options={[
            {
              value: AIAgentType.RECOMMENDED,
              label: 'Recommended Action',
              disabled: !hasUpstreamShipment,
            },
            { value: AIAgentType.CUSTOMIZED, label: 'Custom Action' },
          ]}
        />
      </div>

      {agentType === AIAgentType.CUSTOMIZED && (
        <div className={styles.customizationSection}>
          <label className={styles.sectionLabel}>AI Agent</label>
          <Select
            value={agentId || undefined}
            onChange={handleAgentIdChange}
            placeholder={
              loadingAgents ? 'Loading agents...' : 'Select an agent'
            }
            disabled={isReadOnly || loadingAgents}
            style={{ width: '100%' }}
            className='agentSelect'
            labelRender={() => {
              // Disabling the Select while the agent list loads doesn't stop
              // AntD from rendering *some* label for the current value - with
              // no options loaded yet it would otherwise fall back to the raw
              // agent_id (a UUID). Same for a failed fetch, which previously
              // left agents=[] permanently with no visible indication
              // anything went wrong. Neither case means the agent doesn't
              // exist - only that we can't resolve its name right now -
              // so show that plainly instead of a raw id. Agents genuinely
              // missing from the loaded list (pagination limit, deleted
              // agent) are a separate, not-yet-fixed case.
              if (loadingAgents) return 'Loading agent...';
              const selectedAgent = agents.find((a) => a.id === agentId);
              if (agentsLoadError && !selectedAgent?.name) {
                return 'Unable to load agent name';
              }
              return selectedAgent ? formatAgentLabel(selectedAgent) : agentId;
            }}
          >
            {(['Inbound', 'Outbound'] as const).map((direction) => {
              const group = agents.filter((a) => {
                if (a.direction !== direction) return false;
                if (channelType === AIChannelType.CALL)
                  return !!a.channel?.some((c) => c.type === 'phone');
                if (channelType === AIChannelType.EMAIL)
                  return !!a.channel?.some((c) => c.type === 'email');
                return (
                  !!a.channel?.some((c) => c.type === 'phone') &&
                  !!a.channel?.some((c) => c.type === 'email')
                );
              });
              if (group.length === 0) return null;
              return (
                <Select.OptGroup key={direction} label={direction}>
                  {group.map((agent) => (
                    <Select.Option key={agent.id} value={agent.id}>
                      <span className={layout.compactRow}>
                        <span className={layout.optionIcon}>
                          {direction === 'Inbound' ? (
                            <InboundIcon
                              size={14}
                              color='var(--text-secondary)'
                            />
                          ) : (
                            <OutboundIcon
                              size={14}
                              color='var(--text-secondary)'
                            />
                          )}
                        </span>
                        <span className={layout.optionName}>
                          {formatAgentLabel(agent)}
                        </span>
                      </span>
                    </Select.Option>
                  ))}
                </Select.OptGroup>
              );
            })}
          </Select>
          <p className={layout.footerHint}>
            Create and customize{' '}
            <a
              href='/settings/ai-agent'
              target='_blank'
              rel='noreferrer'
              className={layout.textLink}
            >
              AI agent settings
            </a>
          </p>
        </div>
      )}

      {showRecipient && (
        <div className={styles.customizationSection}>
          <label className={styles.sectionLabel}>Recipient</label>
          <Select
            value={contactId || undefined}
            onChange={handleContactIdChange}
            placeholder={
              loadingContacts ? 'Loading contacts...' : 'Select a contact'
            }
            disabled={isReadOnly || loadingContacts}
            style={{ width: '100%' }}
            className='agentSelect'
            showSearch
            optionFilterProp='label'
          >
            {visibleContacts.map((contact) => (
              <Select.Option
                key={contact.value}
                value={contact.value}
                label={contact.label}
                disabled={contact.disabled}
              >
                {contact.label}
              </Select.Option>
            ))}
          </Select>
        </div>
      )}

      {showReplySettings && (
        <div className={styles.customizationSection}>
          {showDontWait && (
            <Checkbox
              checked={dontWait}
              disabled={isReadOnly}
              onChange={(e) => notifyChange({ dont_wait: e.target.checked })}
            >
              Don&apos;t wait for a reply
            </Checkbox>
          )}
          {dontWait ? (
            <p className={layout.footerHint}>
              The workflow continues right after the email is sent. Add a Wait
              for reply step to act on the answer.
            </p>
          ) : (
            <>
              <label className={styles.sectionLabel}>Wait for reply</label>
              <WaitDurationField
                seconds={currentAICallData.reply_wait_seconds}
                onChange={(seconds) =>
                  notifyChange({ reply_wait_seconds: seconds })
                }
                disabled={isReadOnly}
                placeholder='1 hour (default)'
                amountLabel='Wait for reply'
                emptyUnit={IntervalUnit.MINUTES}
              />
              <p className={layout.footerHint}>
                If nobody replies in time, the workflow continues with Issue
                Resolved false.
                {channelType === AIChannelType.USER_PREFERENCE &&
                  ' Applies when the contact is emailed.'}
              </p>
            </>
          )}
        </div>
      )}

      {agentType === AIAgentType.CUSTOMIZED &&
        agentId &&
        agentId !== 'default' && (
          <div className={styles.customizationSection}>
            {loadingAgentDetails ? (
              <div className={layout.loading}>
                <Spin />
              </div>
            ) : selectedAgentDetails ? (
              <AgentDetailsReadOnly
                agent={selectedAgentDetails}
                collectOptions={collectOptions}
                escalateWhenOptions={escalateWhenOptions}
                userVariablesError={userVariablesError}
              />
            ) : (
              <div className={layout.emptyState}>
                Failed to load agent details
              </div>
            )}
          </div>
        )}
    </>
  );
};
